import { ScrollView } from 'react-native';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { spacing } from '@tabbio/tokens';
import { CVSharePanel, type CvVisibilityOptions } from '../../../components/cv/CVSharePanel';
import { useTheme } from '../../../hooks/useTheme';
import { useCanDownloadCvTarget } from '../../../hooks/useCanDownloadCvTarget';
import { useCvExportEntitlement } from '../../../hooks/useCvExportEntitlement';
import { downloadCvExport, type ExportFormat } from '../../../lib/cv-export';
import { useCVData } from '../../../lib/cv-mock-data';
import { getCvShareVisibilityDefaults } from '../../../lib/cv-privacy';
import { buildPublicCvRouteUrl } from '../../../lib/public-web';
import { useUpgradeFromFormSheet } from '../../../hooks/useUpgradeFromFormSheet';

export default function ShareAdvancedScreen() {
  const { theme } = useTheme();
  const { t } = useTranslation('seeker');
  const { profile, target, privacy } = useCVData();
  // A grantee without the export permission sees no Download tab (A03/A07).
  const canDownload = useCanDownloadCvTarget(target);
  const cvExportAccess = useCvExportEntitlement();
  const openUpgrade = useUpgradeFromFormSheet();
  const initialVisibility = useMemo(
    () => getCvShareVisibilityDefaults(privacy),
    [privacy],
  );
  const publicUrl = useMemo(
    () => (profile.username ? buildPublicCvRouteUrl(profile.username) : null),
    [profile.username],
  );

  const handleDownload = async (
    format: ExportFormat,
    options: { variantId?: string | null; visibility: CvVisibilityOptions },
  ) => {
    await downloadCvExport(format, {
      target,
      variantId: options.variantId,
      visibility: options.visibility,
    });
  };

  return (
    <ScrollView
      contentInsetAdjustmentBehavior="automatic"
      nestedScrollEnabled
      style={{ flex: 1, backgroundColor: theme.background }}
      contentContainerStyle={{
        paddingTop: 16,
        paddingBottom: 48,
        paddingHorizontal: spacing[4],
      }}
      showsVerticalScrollIndicator={false}
    >
      <CVSharePanel
        target={target ?? 'master'}
        title={profile.name || t('cv.title')}
        publicUrl={publicUrl}
        initialVisibility={initialVisibility}
        canDownloadFormat={(format) => !cvExportAccess.isFormatLocked(format)}
        isFormatLoading={cvExportAccess.isFormatLoading}
        onLockedFormat={() => openUpgrade(cvExportAccess.upgradeContext)}
        onDownload={handleDownload}
        canDownload={canDownload}
      />
    </ScrollView>
  );
}
